import { type TranslationKey } from './uk.ts'

export const en: Record<TranslationKey, string> = {
	'nav.login': 'Log in',
	'nav.cabinet': 'My cabinet',
	// admin destinations — grouped in app/components/admin-nav.ts; keep every
	// `nav.*` key in this block (nav.invoices used to live down in the docgen-2
	// section, far from its siblings)
	'nav.admin': 'Admin sections',
	'nav.menu': 'Menu',
	'nav.group.people': 'People',
	'nav.group.learning': 'Learning',
	'nav.group.money': 'Money',
	'nav.group.system': 'System',
	'nav.applications': 'Applications',
	'nav.participants': 'Participants',
	'nav.courses': 'Courses',
	'nav.hints': 'Curator hints',
	'nav.templates': 'Templates',
	'nav.import': 'Import',
	'nav.paymentsInbox': 'Incoming payments',
	'nav.invoices': 'Invoices',
	'nav.fops': 'FOPs',
	'nav.salaryRates': 'Rates',
	'nav.salary': 'Payroll',

	// header user menu (dropdown) + global header search
	'userMenu.label': 'User menu',
	'userMenu.profile': 'Profile',
	'userMenu.logout': 'Log out',
	'search.label': 'Search',

	'home.apply': 'Apply for a course',
	'home.hint':
		'Submit an application — we will get in touch. Course participants sign in with an email code and see their courses, schedule, payments and recordings in the cabinet.',

	'cabinet.title': 'My cabinet',
	'cabinet.welcome': 'Welcome, {name}!',
	'cabinet.empty':
		'Your courses, schedule, and payment status will appear here once you enroll in your first course.',

	'cabinet.myCourses': 'My courses',
	'cabinet.schedule': 'Schedule',
	'cabinet.paymentState': 'Payment state',
	'cabinet.videos': 'Video recordings',
	'cabinet.videosPlaceholder':
		'Video links will appear here — access is currently granted by your curator on request.',
	'cabinet.notLinked':
		'Your account is not linked to a participant record yet — if you have already applied, contact your curator.',

	'participants.title': 'Participants',
	'participants.empty':
		'No participants yet — they will appear from applications.',
	'participants.new': 'New participant',
	'participants.search': 'Search by name or email',
	'participants.notFound': 'No one found for this query.',
	'participants.filter.course': 'Course',
	'participants.filter.status': 'Participation status',
	'participants.filter.any': 'any',
	'participants.filter.apply': 'Filter',
	'bulk.title': 'Bulk actions (for selected)',
	'bulk.op': 'Action',
	'bulk.enroll': 'Enroll in a course',
	'bulk.setStatus': 'Change participation status',
	'bulk.apply': 'Apply',
	'bulk.selectAll': 'Select all',

	'import.title': 'Import from Sheets',
	'import.subtitle':
		'Paste a table from Google Sheets (copy a range with headers: name, email, phone, tg, paid). Re-running the same import creates no duplicates; existing participant records stay unchanged.',
	'import.defaultStatus': 'Participation status for new enrollments',
	'import.paste': 'Data (TSV from Sheets or CSV)',
	'import.placeholder':
		'name\temail\tphone\tpaid\nMaryana Prykladova\tm@example.com\t0671112233\t3000',
	'import.run': 'Import',
	'import.reportTitle': 'Import report',
	'import.report.created': 'participants created: {count}',
	'import.report.matched': 'matched to existing: {count}',
	'import.report.enrolled': 'enrolled in the course: {count}',
	'import.report.payments': 'payments created: {count}',
	'import.report.paymentsSkipped':
		'payments skipped (already present): {count}',
	'import.report.errors': 'skipped rows ({count})',
	'import.report.line': 'row {line}',

	'inbox.title': 'Incoming payments',
	'inbox.subtitle':
		'Paste a statement as 2 tab-separated columns: amount ⇥ purpose (like copying from a sheet). The matcher suggests candidates by the full name in the purpose — you link each payment manually. Rows without a tab or amount are rejected with a reason; a repeated identical row is skipped.',
	'inbox.paste': 'Statement (amount ⇥ purpose)',
	'inbox.placeholder':
		'3000\tcourse payment Maryana Pryklad\n2500\ttuition Petrenko Ivan',
	'inbox.run': 'Parse',
	'inbox.reportTitle': 'Parse report',
	'inbox.report.added': 'added to queue: {count}',
	'inbox.report.skipped': 'skipped (already queued): {count}',
	'inbox.report.rejected': 'rejected rows ({count})',
	'inbox.queueTitle': 'Unlinked queue',
	'inbox.queueEmpty': 'Queue is empty — all incoming payments are sorted.',
	'inbox.item.amount': 'Amount: {amount} UAH',
	'inbox.candidates': 'Candidates',
	'inbox.noCandidates': 'No candidates found.',
	'inbox.manualPath': 'manual path',
	'inbox.candidateNoEnrollments': 'Candidate has no course enrollments —',
	'inbox.bindCourse': 'Course for the payment',
	'inbox.paidAt': 'Payment date',
	'inbox.bind': 'Link payment',
	'inbox.dismissReason': 'Dismiss reason (optional)',
	'inbox.dismiss': 'Dismiss',

	'participant.name': 'Full name',
	'participant.email': 'Email',
	'participant.phone': 'Phone',
	'participant.tgHandle': 'Telegram',
	'participant.social': 'FB / Instagram',
	'participant.notes': 'Notes',
	'participant.category': 'Category',
	'participant.category.general': 'general',
	'participant.category.military': 'military',
	'participant.category.volunteer': 'volunteer',
	'participant.category.dsns': 'SES (rescue service)',
	'participant.preferredChannel': 'Preferred contact channel',
	'participant.courses': "Participant's courses",
	'participant.noCourses': 'Not enrolled in any course yet.',

	'courses.title': 'Courses',
	'courses.empty': 'No courses yet — create the first one.',
	'courses.emptyMine': 'You have no courses of your own yet.',
	'courses.new': 'New course',
	'courses.onlyMine': 'only mine',
	// filter state in the accessible name (a11y): off vs on
	'courses.onlyMineOff': 'only mine',
	'courses.onlyMineOn': 'only mine (on)',

	'course.name': 'Name',
	'course.format': 'Format',
	'course.status': 'Status',
	'course.startDate': 'Start date',
	'course.schedule': 'Schedule',
	'course.nominalPrice': 'Nominal price (UAH)',
	'course.lessonPrice': 'Lesson price (UAH)',
	'course.billingModel': 'Billing model',
	'course.billingModel.per_lesson': 'per lesson',
	'course.billingModel.per_course': 'per course, upfront',
	'course.programMiroLink': 'Program (Miro link)',
	'course.description': 'Description',
	'course.chatLink': 'Course chat (link)',
	'course.fop': 'FOP (for application consent)',
	'course.group': 'Course group',
	'course.curator': 'Curator',
	// salary-2 (spec § 2.2): the class is entered on the COURSE; lessons inherit
	'course.salaryClass': 'Course salary class',
	'course.instructor': 'Instructor',
	'course.groupSize': 'Group size',
	'course.groupSize.small': 'small',
	'course.groupSize.large': 'large',
	'course.participants': 'Course participants',
	'course.noParticipants': 'No participants on this course yet.',

	'course.format.online': 'online',
	'course.format.offline': 'offline',
	'course.format.trip': 'field trip',
	'course.format.tour': 'tour',

	'course.status.upcoming': 'upcoming',
	'course.status.ongoing': 'ongoing',
	'course.status.finished': 'finished',

	'participation.status': 'Participation status',
	'participation.status.interested': 'interested',
	'participation.status.form': 'application',
	'participation.status.considering': 'considering',
	'participation.status.requisites': 'requisites',
	'participation.status.dropped': 'dropped',
	'participation.status.studying': 'studying',
	'participation.status.finished': 'finished',
	'participation.individualPrice': 'Individual price (UAH)',
	'participation.individualPrice.per_lesson':
		'Individual price per lesson (UAH)',
	'participation.individualPrice.per_course':
		'Individual price per course (UAH)',
	'participation.priceUnit.per_lesson': 'UAH/lesson',
	'participation.add': 'Add participant',
	'participation.remove': 'Remove from course',

	'auth.loginTitle': 'Welcome back!',
	'auth.loginSubtitle': 'Enter your email — we will send you a login code.',
	'auth.email': 'Email',
	'auth.sendCode': 'Send a code',
	'auth.verifyLoginTitle': 'Check your email',
	'auth.verifyLoginSubtitle': "We've sent a login code to your address.",
	'auth.resendCode': 'Send the code again',
	'auth.codeResent': 'A new code has been sent — check your email.',
	'auth.signupTitle': 'Welcome!',
	'auth.loginWithGoogle': 'Log in with Google',
	'auth.signupWithGoogle': 'Sign up with Google',
	'auth.connectWithGoogle': 'Connect Google',

	'onboarding.title': 'Welcome aboard, {email}!',
	'onboarding.subtitle': 'Please fill in your profile details.',
	'onboarding.username': 'Username',
	'onboarding.name': 'Name',
	'onboarding.imageAlt': 'Profile photo',
	'onboarding.agree':
		'Do you agree to our Terms of Service and Privacy Policy?',
	'onboarding.remember': 'Remember me',
	'onboarding.submit': 'Create an account',

	'apply.title': 'Course application',
	'apply.subtitle': "Fill in the form — we'll get in touch.",
	'apply.course': 'Course',
	'apply.courses': 'Courses (pick one or more)',
	'apply.knowsChannels': "Do you know Petro's FB and Telegram?",
	'apply.comment': 'Comment',
	'apply.consent': 'I consent to personal data processing ({fop})',
	'apply.consentNoFop': 'I consent to personal data processing',
	'apply.submit': 'Submit application',
	'apply.success': "Application sent! We'll contact you soon.",
	'apply.noCourses': 'No courses are open for enrollment right now.',
	'apply.loginHint': 'Already registered?',
	'apply.loginHintLink': 'Log in — the form will fill itself',
	'apply.prefillNote': 'Fields are pre-filled from your account.',
	'apply.sources': 'How did you hear about us?',

	// «Source» field values — shared by the /apply form and the admin card
	'source.telegram': 'Telegram',
	'source.facebook': 'Facebook',
	'source.instagram': 'Instagram',
	'source.tiktok': 'TikTok',
	'source.youtube': 'YouTube',
	'source.friend_referral': 'Referred by a friend',

	'applications.title': 'Applications',
	'applications.empty': 'No applications yet.',
	'applications.board': 'Application funnel',
	'applications.unassigned': 'unassigned',

	'application.title': 'Application',
	'application.sources': 'Source',
	'application.status': 'Status',
	// funnel statuses v2 — the curator's confirmed list (batch 2 Б1)
	'application.status.new': 'New',
	'application.status.contacted': 'Contacted',
	'application.status.prepayment': 'Prepayment',
	'application.status.paid_course': 'Paid in full',
	'application.status.subsidized': 'Free of charge',
	'application.status.plans_to_pay': 'Plans to pay',
	'application.status.waiting_list': 'Waiting list',
	'application.status.invoice': 'Invoice sent',
	'application.status.declined': 'Declined',
	'application.inGroup': 'in group',
	'application.assignee': 'Handled by',
	'application.disposition': 'First contact outcome',
	'application.disposition.requisites_given': 'requisites given',
	'application.disposition.pay_later': 'will pay later',
	'application.disposition.will_pay_on_date': 'will pay on a date → remind',
	'application.disposition.invite_next_cohort': 'invite to next cohort',
	'application.disposition.refusal': 'refusal',
	'application.disposition.no_response': 'no response',
	'application.disposition.link_issued': 'link issued',
	'application.disposition.curator_introduced': 'curator introduced',
	'application.paymentAmount': 'Payment amount (UAH)',
	'application.takeOver': 'Take over',
	'application.takenBy': 'Handled by: {name}',
	'application.alreadyTaken': 'Already taken by {name}',
	'application.history': 'History',
	'application.addNote': 'Add note',
	'application.consentLabel': 'Consent (FOP)',
	'application.clientType.new': '🆕 new client',
	'application.clientType.returning': '⭐ returning participant',
	'application.wasSubsidized': '🎁 previously free-of-charge',
	'application.prepaymentHint':
		'💡 Returning participant — prepayment optional',
	'application.event.submitted': 'Application submitted',
	'application.event.assigned': 'Assigned',
	'application.event.status_changed': 'Status changed',
	'application.event.disposition_set': 'Contact outcome',
	'application.event.payment_noted': 'Payment noted',
	'application.event.note': 'Note',
	'application.event.invoice_requested': 'Invoice requested',
	'application.invoice.heading': 'Invoice for employer',
	'application.invoice.contact': 'Payer contact (for the corporate curator)',
	'application.invoice.optionalHint':
		'Fields below are the corporate curator’s stage — optional',
	'application.invoice.requisites': 'Payer requisites',
	'application.invoice.fop': 'FOP for the invoice',
	'application.invoice.courseFopHint': 'Course FOP: {fop}',
	'application.invoice.lastRequest': 'Last request',
	'application.invoice.submit': 'Request invoice',

	// docgen-1: FOP executors config (/settings/fops) — data, not code (#79);
	// no deletion — deactivation (spec § 2.6)
	'fops.title': 'FOP executors',
	'fops.new': 'New FOP',
	'fops.empty': 'No FOP executors yet — add the first one.',
	'fops.actions': 'Actions',
	'fop.name': 'FOP full name',
	'fop.inn': 'Tax ID (IPN)',
	'fop.iban': 'IBAN',
	'fop.bank': 'Bank',
	'fop.certificate': 'Certificate / extract no.',
	'fop.taxStatusLine': 'Tax status line (as in the template)',
	'fop.address': 'Address',
	'fop.kved': 'KVED (activity code)',
	'fop.signerName': 'Signer (“Surname I. P.”)',
	// docgen-3: contract requisites (optional per-FOP)
	'fop.phone': 'Phone (for contract requisites)',
	'fop.edrBasisClause': 'Basis of activity (EDR clause for the contract)',
	'fop.status': 'Status',
	'fop.active': 'active',
	'fop.inactive': 'inactive',
	'fop.deactivate': 'Deactivate',
	'fop.activate': 'Activate',

	// salary-1: lesson salary classes (spec § 2.2)
	'salary.class.online': 'Online lesson',
	'salary.class.offline': 'Offline lesson',
	'salary.class.offline_snack': 'Offline lesson with a snack',
	'salary.class.offline_two_snacks_or_5h':
		'Offline lesson with two snacks or 5+ hours',
	// salary-1: non-lesson rate keys (spec §§ 2.1, 2.5, 2.6)
	'salary.rate.participant': 'Per lesson participant',
	'salary.rate.application': 'Per processed new course application',
	'salary.rate.screencaster_recording': 'Per lesson recording (screencaster)',
	// instructor-comp-spec § 2.7: the human label of an instructor-compensation
	// row is an i18n key (the wire carries `kind`, the text lives here)
	'salary.rate.instructorComp': 'Instructor compensation',
	// salary-1: rates surface (/settings/salary-rates) — edit-only (§ 2.3)
	'salaryRates.title': 'Salary rates',
	'salaryRates.intro':
		'Seven fixed rates in whole hryvnias. Keys are never created or deleted here — only the amounts are edited. An empty field means “leave this key alone”: an unconfigured rate shows up as a gap in the report and blocks statement fixation, while clearing an already-filled field does NOT un-configure the rate — change the amount instead. Zero is a deliberate rate value, not “not configured”.',
	'salaryRates.rate': 'Rate',
	'salaryRates.amount': 'Amount, UAH',
	'salaryRates.updatedAt': 'Updated',
	'salaryRates.updatedBy': 'Updated by',
	'salaryRates.notConfigured': 'not configured',
	// salary-2: live monthly report (/salary) — spec §§ 3 pp. 3-4, 6
	'salary.report.title': 'Monthly accruals',
	'salary.report.intro':
		'A live calculation: it stores no rows, so attendance and salary-class edits show up immediately. Every recipient of the month is listed — lesson curators, screencasters and the actors who processed applications. Gaps are shown separately and never enter the totals.',
	'salary.report.month': 'Month',
	'salary.report.show': 'Show',
	'salary.report.recipient': 'Recipient',
	'salary.report.lessons': 'Lessons',
	'salary.report.participants': 'Participants',
	'salary.report.recordings': 'Recordings',
	'salary.report.applications': 'Applications',
	'salary.report.total': 'Total, UAH',
	'salary.report.monthTotal': 'Month total, UAH',
	'salary.report.empty': 'No accruals and no gaps for this month.',
	'salary.report.gaps': 'Gaps of the month',
	'salary.report.fixationBlocked': 'fixation blocked',
	'salary.report.fixed': 'fixed',
	// gap wording — one source for every report surface (§§ 2.4, 3)
	'salary.gap.lessonsWithoutClass':
		'lessons without a salary class: {lessons} (participant accruals withheld: {withheld})',
	'salary.gap.lessonsNotYetHeld':
		'lessons of the month not held yet: {lessons}',
	'salary.gap.lessonsWithoutCurator': 'lessons without a curator: {lessons}',
	'salary.gap.applicationsWithoutAttribution':
		'applications without attributable processing: {applications} (no actor: {nullActor}, no typed event: {untyped})',
	'salary.gap.rateNotConfigured': 'rate not configured: {keys}',
	// salary-2: the OQ-B6 detector (§ 2.6) — not an accrual, a visible suspicion
	'salary.warn.maybeSecondCurator': 'A second curator may be missing',
	'salary.warn.lesson':
		'{date}, {course} — {curator}: participants who consumed it {participants}',
	// salary-2: recipient drill-down (§ 3 p.4)
	'salary.detail.title': 'Detail: {name}',
	'salary.detail.back': '← back to the monthly report',
	'salary.detail.date': 'Date',
	'salary.detail.course': 'Course',
	'salary.detail.kind': 'What for',
	'salary.detail.rate': 'Rate, UAH',
	'salary.detail.participants': 'Participants',
	'salary.detail.participantsCell': '{participants} × {rate}',
	'salary.detail.amount': 'Amount, UAH',
	'salary.detail.empty': 'No accruals for this month.',
	// salary-3: statement fixation + carry-forward (spec §§ 2.4, 2.8, 3 item 5)
	'salary.fix.button': 'Fix the statement',
	'salary.fix.notYetHeld':
		'Lessons of the month not yet held: {lessons} — the statement will be fixed without them, the delta catches up next month',
	'salary.fix.blocked':
		'Fixation blocked: not all rates are configured (see the month gaps in the report)',
	'salary.statement.title': 'Fixed statement',
	'salary.statement.fixedAt': 'Statement fixed {date} — {name}',
	'salary.statement.recipientName': 'Recipient on the statement: {name}',
	'salary.statement.recipientRenamed':
		'Recipient on the statement: {name} (now: {current})',
	'salary.statement.hint':
		'The current calculation differs from the fixed statement — the delta will enter the next (unfixed) statement',
	'salary.statement.void': 'Void',
	'salary.statement.voidConfirm': 'Sure to void?',
	'salary.statement.voidedRow':
		'Voided statement: fixed {fixedDate}, voided {voidedDate} ({voidedBy}), total {total} UAH',
	// instructor-comp-spec § 6.1 (voided channel): the same row WITHOUT the sum
	'salary.statement.voidedRowNeutral':
		'Voided statement: fixed {fixedDate}, voided {voidedDate} ({voidedBy})',
	// instructor-comp-spec § 2.7: sub-line in the recipient cell — the row total
	// includes the component, which has no column of its own
	'salary.report.instructorComp': 'incl. instructor compensation: {amount} UAH',
	// arrangement terms — `full` layer only (§ 2.7), FROZEN snapshot values
	'salary.instructor.percentTerms':
		'{percent}% of {base}, weight {weight}% · income {income} UAH, expenses: auto {auto} UAH + manual {manual} UAH',
	'salary.instructor.baseIncome': 'income',
	'salary.instructor.baseProfit': 'profit',
	'salary.instructor.fixedCourseTerms':
		'fixed per course {amount} UAH, weight {weight}%',
	'salary.instructor.fixedLessonTerms':
		'fixed per lesson {amount} UAH × {lessons}, weight {weight}%',
	'salary.carry.title': 'Carried over from previous months',
	'salary.carry.from': 'carried from {month}',
	'salary.carry.reportLine': 'carried over from previous months: {amount} UAH',

	// instructor-comp IC-1b (spec §§ 6, 9 п.2): the comp-rights grant surface on
	// the user profile — owner-only. Role LABELS deliberately live in the
	// database (`Role.description`, migration ic1a), not here
	'compRights.section': 'Instructor compensation rights',
	// the second sentence names WHAT is irreversible (GDPR ruling #136): the log
	// entry, not the account. It deliberately says "the log no longer blocks
	// account deletion" rather than "the account can be deleted at any time" —
	// the latter would be false: two other Restrict edges on User remain (salary
	// statements, the course-instructor fact). The first sentence is qualified
	// too: the log answers BY NAME only while the account exists; after erasure
	// an anonymous fact is what is left. Mirror of uk.ts
	'compRights.intro':
		'Only the owner grants and revokes. Every change is written to an audit log (who received which role, who acted, when) — so that "who could see the terms in a given month" stays answerable later; by name only while the account exists, and after its deletion an anonymous fact is what remains. The log entry is what is irreversible, not the account: the log no longer blocks account deletion — after the deletion (which the person performs in their own settings) the entry stays on in anonymised form: "at time T an actor of such-and-such role granted or revoked such-and-such right".',
	'compRights.granted': 'granted',
	'compRights.notGranted': 'not granted',
	'compRights.coveredByOwner': 'holds the right through the owner role',
	'compRights.grant': 'Grant',
	'compRights.grantConfirm': 'Really grant?',
	'compRights.revoke': 'Revoke',

	// docgen-2: invoices (/invoices) — request queue, creation form, invoice
	// page, docx download. Registry tab, paid/void — docgen-3
	// (`nav.invoices` lives up top with the rest of the nav keys)
	'invoices.title': 'Invoices',
	'invoices.tabs': 'Invoice tabs',
	'invoices.requestsTab': 'Requests',
	'invoices.registryTab': 'Registry',
	'invoices.requests.empty': 'The queue is empty — no new invoice requests.',
	'invoices.requests.requestText': 'Request text',
	'invoices.requests.create': 'Create invoice',
	'invoice.new.title': 'New invoice',
	'invoice.new.requestReference': 'Request (reference)',
	'invoice.new.course': 'Course',
	'invoice.fop': 'Executing FOP',
	'invoice.fop.placeholder': '— select a FOP —',
	'invoice.customerName': 'Customer (name)',
	'invoice.customerSignerName': 'Customer signer ("represented by …")',
	'invoice.customerDetails': 'Payer requisites',
	'invoice.contractNumber': 'Contract no.',
	'invoice.contractDate': 'Contract date',
	'invoice.addendumNumber': 'Addendum no.',
	'invoice.stage': 'Stage',
	'invoice.stage.prepayment': 'prepayment',
	'invoice.stage.postpayment': 'final payment',
	'invoice.stage.full': 'full',
	'invoice.stage.other': 'other',
	'invoice.amount': 'Amount, UAH',
	'invoice.amountValue': '{amount} UAH',
	'invoice.number': 'Invoice number',
	'invoice.numberHint':
		"auto number from the FOP's series; edited manually — the accountant's manual number",
	'invoice.numberFromIssueDate':
		'the number will be assigned from the invoice date on save',
	'invoice.issueDate': 'Invoice date',
	'invoice.serviceDate': 'Service date',
	'invoice.validUntil': 'Valid until',
	'invoice.serviceDescription': 'Service description',
	'invoice.title': 'Invoice no. {number}',
	'invoice.noFile': 'no file',
	'invoice.regenerate': 'Regenerate',
	'invoice.files': 'Generated files',
	'invoice.files.empty': 'No file generated yet.',
	'invoice.download': 'Download',
	'invoice.status': 'Status',
	'invoice.status.issued': 'issued',
	'application.event.invoice_issued': 'Invoice issued',

	// docgen-3: registry, lifecycle (§ 2.6), act (§ 3.2), contract (§ 3.3),
	// act reminder (§ 2.7 — the text NAMES the configured trigger)
	'invoice.status.paid': 'paid',
	'invoice.status.void': 'voided',
	'invoice.paidAt': 'Paid on',
	'invoice.voidedAt': 'Voided on',
	'invoice.lifecycle': 'Invoice actions',
	'invoice.markPaid': 'Mark paid',
	'invoice.void': 'Void',
	'invoice.voidConfirm': 'Really void?',
	'invoice.generate': 'Document generation',
	'invoice.generateAct': 'Generate act',
	'invoice.generateContract': 'Generate contract',
	'invoices.registry.filter.all': '— all —',
	'invoices.registry.filter.month': 'Month',
	'invoices.registry.filter.apply': 'Filter',
	'invoices.registry.filter.reset': 'Reset filters',
	'invoices.registry.totals':
		'Filtered totals: {count} invoice(s), {amount} UAH',
	'invoices.registry.empty': 'No invoices match this filter.',
	'invoices.registry.act': 'Act',
	'invoices.registry.actDone': 'act ✓',
	'invoices.registry.actReminder.service_date_passed':
		'lesson delivered, act not generated',
	'invoices.registry.actReminder.invoice_paid':
		'invoice paid, act not generated',
	'act.title': 'Act for invoice no. {number}',
	'act.partiesHint':
		'The parties come from the invoice: the executor — from the FOP requisites snapshot, the customer — from the invoice fields.',
	'act.relatedInvoices': 'Invoices under this contract (reference)',
	'act.relatedInvoicesHint':
		'Same FOP and contract no.; enter the act amount yourself — there is no auto-summing.',
	'act.date': 'Date of issue',
	'act.amount': 'Act amount, UAH',
	'act.contractRefClause': 'Contract reference (clause)',
	'act.generate': 'Generate act',
	'contract.title': 'Contract for invoice no. {number}',
	'contract.effectiveDate': 'Effective date',
	'contract.addendumDate': 'Addendum date',
	'contract.contractEndDate': 'Valid until',
	'contract.snapshotIncompleteHint':
		'Executor requisites are incomplete — the contract will contain gaps; complete the FOP config and re-issue the invoice.',
	'contract.customerIntroClause': 'Customer intro clause',
	'contract.rows': 'Addendum details',
	'contract.rows.detail': 'Service details',
	'contract.rows.paymentDue': 'Payment due',
	'contract.rows.deliveryDue': 'Delivery due',
	'contract.rows.price': 'Price, UAH (excl. VAT)',
	'contract.rows.add': 'Add row',
	'contract.rows.remove': 'Remove row {number}',
	'contract.total': 'Total',
	'contract.generate': 'Generate contract',

	'payments.title': 'Payments',
	'payments.add': 'Record payment',
	'payments.none': 'No payments yet.',
	'payment.amount': 'Amount (UAH)',
	'payment.type': 'Type',
	'payment.type.advance': 'advance',
	'payment.type.full': '100%',
	'payment.type.free_of_charge': 'free of charge (=0)',
	'payment.type.discount': 'discount',
	'payment.payer': 'Payer',
	'payment.payer.self': 'self',
	'payment.payer.employer': 'employer',
	'payment.payer.gift': 'gift',
	'payment.note': 'Note',
	'payment.lesson': 'Lesson (optional)',
	'payment.wholeCourse': 'whole course',
	'payment.forLesson': 'lesson {date}',
	'payment.paidAt': 'Payment date',
	'payment.paid': 'paid',
	'payment.unpaid': 'unpaid',
	'payment.paidTotal': 'Paid total',
	'payment.accruedPaidDebt': 'accrued {accrued} / paid {paid} / debt {debt}',
	'payment.audit': 'Change history',
	'payment.void': 'Void payment',
	'payment.voidConfirm': 'Really void?',
	'payment.voidedInfo': 'voided — {name}, {date}',

	'lessons.title': 'Lessons',
	'lessons.none': 'No lessons yet — add the first one.',
	'lessons.numbered': 'Lesson {number}',
	'lessons.date': 'Date',
	'lessons.lessonTitle': 'Title (optional)',
	'lessons.add': 'Add lesson',
	'lesson.screencaster': 'Screencaster',
	// salary-2 (spec § 2.2): effective class = own ?? course; the form shows
	// BOTH the value and where it came from
	'lesson.salaryClass': 'Salary class',
	'lesson.salaryClass.fromCourse': 'from the course',
	'lesson.salaryClass.own': 'own',
	'lesson.salaryClass.effective': 'Salary class: {class} ({source})',
	'lesson.salaryClass.unset': 'Salary class: not set',
	'lesson.salaryClass.none': 'not set',
	'attendance.title': 'Attendance',
	'attendance.cellLabel': 'Attendance: {name} — {date}',
	'attendance.unmarked': '—',
	'attendance.present': 'present',
	'attendance.absent': 'absent',
	'attendance.recording': 'got the recording',

	'video.title': 'Video title',
	'video.url': 'Link (URL)',
	'video.filePath': 'File in the video store (local)',
	'video.local': 'local file',
	'video.xorHint':
		'Fill exactly one: a link to an external video OR a file path in the video store (data/videos/).',
	'video.add': 'Add video',
	'video.none': 'No videos yet.',
	'video.access.request': 'Request access to recordings',
	'video.access.requested': 'Access granted — recordings below.',
	'video.access.none':
		'Recordings become available on request — press the button.',
	'video.access.grants': 'Video accesses',
	'video.access.grant': 'Grant access',
	'video.access.grantedVia.request': 'on request',
	'video.access.grantedVia.manual': 'manually',
	'chat.invite': 'Join the course chat',
	'chat.inviteHint': 'The chat link appears after payment.',
	'chat.inChat': 'in chat',
	'chat.paidNotInChat': 'paid but not in chat',

	'hints.title': 'Curator hints',
	'hints.noPressure':
		'An internal digest — no pressure: the system never sends anything itself, the curator looks and decides.',
	'hints.none': 'No hints — everything is under control.',
	'hints.onlyMine': 'only mine',
	// filter state in the accessible name (a11y): off vs on
	'hints.onlyMineOff': 'only mine',
	'hints.onlyMineOn': 'only mine (on)',
	'hints.trigger': 'Trigger',
	'hints.since': 'since {date}',
	'hints.lastActivity': 'last activity: {date}',
	'hints.lessonAt': 'lesson: {date}',
	'hints.shownOf': 'showing {shown} of {total} — oldest first',
	'hints.shownOfRecent': 'showing {shown} of {total} — newest first',
	'hints.shownOfSoonest': 'showing {shown} of {total} — soonest first',
	'hints.debt': 'debt {debt} UAH (paid {paid} of {price})',
	'hints.firstContactDue.title': 'First contact overdue',
	'hints.firstContactDue.trigger':
		'application assigned more than {hours}h ago, first-contact disposition not set',
	'hints.paymentDue.title': 'Payment expected',
	'hints.paymentDue.trigger': 'studying, paid less than accrued',
	'hints.paidNotInChat.title': 'Paid but not in chat',
	'hints.paidNotInChat.trigger':
		'payment recorded but the "in chat" mark is missing — invite to the course chat',
	'hints.missedTwice.title': 'Missed lessons',
	'hints.missedTwice.trigger': 'studying, marked "absent" on 2 or more lessons',
	'hints.missedCount': 'lessons missed: {count}',
	'hints.afterLesson.title': 'After a lesson',
	'hints.afterLesson.trigger':
		'the lesson just took place — send the recording link and materials (Miro board, etc.) to the chat, if available',
	'hints.upcomingLesson.title': 'Next lesson',
	'hints.upcomingLesson.trigger':
		'remind the group about the lesson and run a readiness poll in the chat',
	'hints.upcomingLesson.horizon.hour': 'within an hour',
	'hints.upcomingLesson.horizon.tomorrow': 'tomorrow',
	'hints.upcomingLesson.horizon.threeDays': 'in 3 days',
	'hints.upcomingLesson.horizon.week': 'within a week',

	'templates.title': 'Message templates',
	'templates.noAutoSend':
		'Templates are for manual copy-paste: the system never sends anything itself.',
	'templates.empty': 'No templates yet — create the first one.',
	'templates.new': 'New template',
	'templates.renderTitle': 'Message from a template',
	'templates.selectTemplate': 'Template',
	'templates.copy': 'Copy',
	'templates.copied': 'Copied!',
	'templates.copyFailed':
		'Could not copy automatically — select and copy the text manually.',
	'templates.noneHint': 'No templates yet — create one in:',
	'template.name': 'Name',
	'template.body': 'Template text',
	'template.placeholdersHint':
		'Placeholders: {{учасник}}, {{курс}}, {{ціна}}, {{борг}}, {{розклад}}',

	'commlog.title': 'Communication history',
	'commlog.none': 'No entries yet.',
	'commlog.add': 'Add entry',
	'commlog.channel': 'Channel',
	'commlog.note': 'Note',
	'commlog.channel.messenger': 'messenger',
	'commlog.channel.phone': 'phone',
	'commlog.channel.email': 'email',
	'commlog.channel.in_person': 'in person',
	'commlog.channel.other': 'other',

	'action.create': 'Create',
	'action.save': 'Save',
	'action.edit': 'Edit',
	'action.cancel': 'Cancel',
	'action.delete': 'Delete',
	'action.deleteConfirm': 'Really delete?',

	'error.forbidden': 'You do not have access to this page.',

	// server-side form errors (schema factories + action formErrors/fieldErrors)
	'error.apply.consentRequired': 'You must consent to personal data processing',
	'error.application.invoiceContactRequired':
		'Enter the payer contact for the corporate curator',
	'error.application.invoiceStatusStale':
		'Application is no longer in Invoice status — refresh the page',
	'error.apply.courseRequired': 'Pick at least one course',
	'error.apply.courseUnavailable': 'This course is not open for enrollment',
	'error.apply.partialFailure':
		'Could not submit an application for: {courses}. Please try again — already-created applications are not duplicated.',
	'error.bulk.statusRequired': 'Choose the status to set',
	'error.participantNotFound': 'Participant not found',
	'error.courseNotFound': 'Course not found',
	'error.fopNotFound': 'FOP not found',
	'error.fopToggleInvalid': 'Invalid FOP status action',
	'error.compRoleInvalid': 'Invalid instructor compensation rights action',
	// salary-1 (spec § 2.3, invariant «а»): rate reads are fail-closed
	'error.salaryRates.unknownKey':
		'Unknown salary rate key in the database: {keys}. This surface edits only the seven known keys — the stray row has to be removed from the data.',
	'toast.salaryRates.saved': 'Rates saved: {count}',
	'toast.salaryRates.unchanged': 'Nothing changed',
	// salary-2: a malformed month is rejected at the edge, never swapped silently
	'error.salary.badMonth':
		'Invalid month: “{month}”. The expected format is YYYY-MM.',
	'error.salary.recipientNotFound': 'Recipient not found',
	// instructor-comp-spec § 6 (option A): only someone who can SEE the
	// instructor component may fix or void a statement carrying it
	'error.salary.compRightRequired':
		'This statement requires the additional instructor-compensation right',
	// salary-3: typed fixation/void errors (§§ 2.3 "c", 2.4)
	'error.salary.alreadyFixed': 'A statement for this month is already fixed',
	'error.salary.fixRatesIncomplete':
		'Fixation blocked: rates not configured — {keys}',
	'error.salary.nothingToFix':
		'Nothing to fix: the month has no accruals, no gaps and no carry-over',
	'error.salary.voidAlready': 'The statement is already voided',
	'error.salary.voidNotFound':
		'No such statement among this recipient’s statements for this month',
	'error.salary.corruptStatement':
		'The fixed statement is corrupt (id: {id}) — its snapshot cannot be read. Display and recalculation stopped.',
	// Restrict edges on User — typed refusal instead of a 500 on account
	// deletion. TWO of them (salary statements, the instructor fact — the
	// rights-grant log dropped out with GDPR ruling #136, its edge is SetNull
	// now), so the text names the CLASS of the cause and lists examples, not a
	// closed set: P2003 does not say WHICH edge fired
	'error.user.deleteKeptHistory':
		'Cannot delete the account: it is tied to history the system keeps (for example: fixed salary statements, course-instructor facts)',
	'toast.salary.fixed': 'Statement fixed',
	'toast.salary.voided': 'Statement voided',
	'error.invoiceNotFound': 'Invoice not found',
	'error.invoiceVoided':
		'Invoice is voided — document generation is unavailable',
	'error.invoice.fopRequired': 'Select an executing FOP',
	'error.invoice.fopUnavailable':
		'The selected FOP is unavailable (inactive or deleted)',
	'error.invoice.numberTaken': 'Number {number} is already used for this FOP',
	'error.invoice.invalidDate': 'Invalid date (no such day in that month)',
	'toast.invoice.created': 'Invoice {number} created',
	'toast.invoice.createdNoFile':
		'Invoice {number} created, but the file was not generated — regenerate it on the invoice page',
	'toast.invoice.createdNoFileDeterministic':
		'Invoice {number} created, but the file was not generated (template error) — needs a fix, regenerating will not help',
	'toast.invoice.regenerated': 'File regenerated',
	'toast.invoice.regenerateFailed':
		'Failed to generate the file — please try again',
	'toast.invoice.regenerateFailedDeterministic':
		'Failed to generate the file — template error, regenerating will not help',
	// docgen-3: lifecycle (§ 2.6) + act/contract generation (§§ 3.2-3.3)
	'error.invoice.payBlocked':
		'The invoice was not marked paid: it is already paid or voided',
	'error.invoice.voidBlocked': 'The invoice is already voided',
	'toast.invoice.paid': 'Invoice {number} marked as paid',
	'toast.invoice.voided': 'Invoice {number} voided',
	'toast.act.generated': 'Act generated',
	'toast.act.failed': 'Failed to generate the act — please try again',
	'toast.act.failedDeterministic':
		'Failed to generate the act — template or data error, retrying will not help',
	'toast.contract.generated': 'Contract generated',
	'toast.contract.failed': 'Failed to generate the contract — please try again',
	'toast.contract.failedDeterministic':
		'Failed to generate the contract — template or data error, retrying will not help',
	'error.participant.emailExists':
		'A participant with this email already exists',
	'error.participant.notEnrolled':
		'The participant is not enrolled in this course',
	'error.payment.voidedNoEdit':
		'A voided payment cannot be edited — record a new one',
	'error.payment.alreadyVoided': 'The payment is already voided',
	'error.payment.notFound': 'Payment not found',
	'error.lesson.wrongCourse': 'The lesson does not belong to this course',
	'error.lesson.hasRecords':
		'The lesson has attendance marks ({attendance}) or payments ({payments}) — deletion is blocked',
	'error.lesson.assigneeNotStaff': 'The selected person is not staff',
	'error.lessonOrParticipantWrongCourse':
		'The lesson or participant does not belong to this course',
	'error.video.exactlyOne':
		'Fill exactly one: a link (URL) or a file path in the video store',
	'error.videoAccess.notEnrolled':
		'The participant is not enrolled — add an enrollment to the course first',
	'error.template.nameExists': 'A template with this name already exists',

	'error.import.headerAndDataRequired':
		'A header row and at least one data row are required',
	'error.import.nameColumnMissing': 'Name column not found (ім’я / піб / name)',
	'error.import.emptyName': 'Empty name — row skipped',
	'error.import.noAnchor': '"{name}": no anchor (email or phone) — row skipped',
	'error.import.anchorConflict':
		'"{name}": the email and phone point to DIFFERENT existing participants — check manually; row skipped',
	'error.import.phoneAmbiguous':
		'"{name}": the phone matches several participants — add an email; row skipped',
	'error.import.rowFailed': '"{name}": saving failed — row skipped',

	'error.inbox.noTab': 'No tab (expected: amount ⇥ purpose)',
	'error.inbox.noAmount': 'Amount not recognized',
	'error.inbox.badAmountFormat':
		'Invalid amount format (number only, optional currency UAH/₴)',
	'error.inbox.conflict': 'This payment was already sorted — refresh the page',
	'error.inbox.badRequest': 'Invalid request — please try again',

	// user-validation schemas (reachable via Google onboarding)
	'error.username.required': 'Username is required',
	'error.username.tooShort': 'Username is too short',
	'error.username.tooLong': 'Username is too long',
	'error.username.invalid':
		'Username can only include letters, numbers, and underscores',
	'error.username.taken': 'A user already exists with this username',
	'error.name.required': 'Name is required',
	'error.name.tooShort': 'Name is too short',
	'error.name.tooLong': 'Name is too long',
	'error.email.required': 'Email is required',
	'error.email.invalid': 'Email is invalid',
	'error.email.tooShort': 'Email is too short',
	'error.email.tooLong': 'Email is too long',
	'error.password.required': 'Password is required',
	'error.password.tooShort': 'Password is too short',
	'error.password.tooLong': 'Password is too long',
	'error.password.mustMatch': 'The passwords must match',
	'error.agreeRequired':
		'You must agree to the terms of service and privacy policy',

	// server toasts
	'toast.apply.title': 'Thank you!',
	'toast.apply.description': 'Application sent.',
	'toast.bulk.title': 'Bulk action',
	'toast.bulk.enrolled':
		'Enrolled in the course: {created} new (already enrolled: {existing})',
	'toast.bulk.statusUpdated':
		'Status updated: {updated} (selected: {selected})',
	'toast.application.taken.title': 'Taken',
	'toast.signup.welcome.title': 'Welcome',
	'toast.signup.welcome.description': 'Thanks for signing up!',
	'toast.auth.failed.title': 'Auth Failed',
	'toast.auth.failed.description':
		'There was an error authenticating with {provider}.',
	'toast.connection.already.title': 'Already Connected',
	'toast.connection.alreadyYours':
		'Your "{username}" {provider} account is already connected.',
	'toast.connection.alreadyOther':
		'The "{username}" {provider} account is already connected to another account.',
	'toast.connection.connected.title': 'Connected',
	'toast.connection.connected.description':
		'Your "{username}" {provider} account has been connected.',
	'toast.connection.initiatorMismatch':
		'Your session changed during the connection attempt. Please try again.',

	'common.language': 'Language',
	'common.loading': 'Loading…',
	'common.notSet': '—',
}
